import ApplicationController from "./application_controller"

/**
 * Search Modal Controller
 *
 * Handles the search modal overlay functionality including:
 * - Opening/closing the modal
 * - Syncing input values between header and modal
 * - Focus management
 * - Search submission
 * - Smooth animations
 *
 * @since 25.06
 */
export default class extends ApplicationController {
  static targets = ["overlay", "headerInput", "modalInput", "searchButton", "autosuggest"]
  static classes = ["hidden"]

  connect() {
    // Set up initial state
    this.isOpen = false
    this.debounceTimer = null
    this.selectedSuggestionIndex = -1
    this.suggestions = []
    
    // Bind event handlers
    this.escapeHandler = this.handleEscape.bind(this)
    this.keyboardShortcutHandler = this.handleKeyboardShortcut.bind(this)
    this.pageShowHandler = this.handlePageShow.bind(this)
    
    // Prevent body scroll when modal is open
    this.originalBodyOverflow = null
    
    // Add global keyboard shortcut listener
    document.addEventListener('keydown', this.keyboardShortcutHandler)
    
    // Add pageshow listener to handle browser back navigation
    window.addEventListener('pageshow', this.pageShowHandler)
    
    // Update placeholder with keyboard shortcut
    this.updatePlaceholderWithShortcut()
    
    // Pre-populate with current search query if it exists
    this.prePopulateSearchQuery()
    
    // Check if modal should be open on page load (handles back navigation)
    this.checkModalStateOnLoad()
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.escapeHandler)
    document.removeEventListener('keydown', this.keyboardShortcutHandler)
    window.removeEventListener('pageshow', this.pageShowHandler)
    
    // Clear any pending debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    
    // Restore body scroll if it was modified
    if (this.originalBodyOverflow !== null) {
      document.body.style.overflow = this.originalBodyOverflow
    }
  }

  // Open the search modal
  openModal() {
    if (this.isOpen) return
    
    this.isOpen = true
    
    // Show overlay with animation
    this.overlayTarget.classList.remove(this.hiddenClass)
    
    // Prevent body scroll
    this.originalBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    
    // Focus the modal input after a brief delay to allow for animation
    requestAnimationFrame(() => {
      this.modalInputTarget.focus()
    })
    
    // Add escape key listener
    document.addEventListener('keydown', this.escapeHandler)
    
    // Sync current header input value to modal (only if header input exists)
    if (this.hasHeaderInputTarget) {
      this.modalInputTarget.value = this.headerInputTarget.value
    }
  }

  // Close the search modal  
  closeModal() {
    if (!this.isOpen) return
    
    this.isOpen = false
    
    // Hide overlay
    this.overlayTarget.classList.add(this.hiddenClass)
    
    // Restore body scroll
    if (this.originalBodyOverflow !== null) {
      document.body.style.overflow = this.originalBodyOverflow
      this.originalBodyOverflow = null
    }
    
    // Remove escape key listener
    document.removeEventListener('keydown', this.escapeHandler)
    
    // Don't return focus to header input to avoid reopening modal
    // Instead, blur the header input to remove focus (only if it exists)
    if (this.hasHeaderInputTarget) {
      this.headerInputTarget.blur()
    }
  }

  // Sync input values between header and modal
  syncInput(event) {
    const sourceInput = event.target
    
    // Only sync if we have a header input target
    if (!this.hasHeaderInputTarget) return
    
    const targetInput = sourceInput === this.headerInputTarget 
      ? this.modalInputTarget 
      : this.headerInputTarget
    
    targetInput.value = sourceInput.value
  }

  // Handle search form submission
  submitSearch(event) {
    // Always prevent default form submission to handle redirect manually
    event.preventDefault()
    
    // If modal is not open and this is triggered from header input, open modal instead
    if (!this.isOpen) {
      this.openModal()
      return
    }
    
    const query = this.modalInputTarget.value.trim()
    
    if (!query) {
      return
    }
    
    // Show loading state
    this.showSearchLoading()
    
    // Redirect to WordPress search results
    const searchUrl = new URL(window.location.origin)
    searchUrl.searchParams.set('s', query)
    
    window.location.href = searchUrl.toString()
  }

  // Show loading state on search button
  showSearchLoading() {
    if (this.hasSearchButtonTarget) {
      const button = this.searchButtonTarget
      button.disabled = true
      
      // Store original content
      if (!button.dataset.originalContent) {
        button.dataset.originalContent = button.innerHTML
      }
      
      // Show spinner
      button.innerHTML = `
        <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `
    }
  }

  // Reset search button (in case navigation fails)
  resetSearchLoading() {
    if (this.hasSearchButtonTarget) {
      const button = this.searchButtonTarget
      button.disabled = false
      
      if (button.dataset.originalContent) {
        button.innerHTML = button.dataset.originalContent
      }
    }
  }

  // Note: Make/model form submission is now handled by the car-finder.js component

  // Prevent modal from closing when clicking inside the content area
  preventClose(event) {
    event.stopPropagation()
  }

  // Handle escape key press
  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault()
      this.closeModal()
    }
  }

  // Handle keyboard shortcuts (Ctrl+K or Cmd+K)
  handleKeyboardShortcut(event) {
    // Check for Ctrl+K (Windows/Linux) or Cmd+K (Mac)
    const isSearchShortcut = (event.ctrlKey || event.metaKey) && event.key === 'k'
    
    if (isSearchShortcut && !this.isOpen) {
      // Don't trigger if user is typing in an input, textarea, or contenteditable element
      const activeElement = document.activeElement
      const isTyping = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      )
      
      if (!isTyping) {
        event.preventDefault()
        this.openModal()
      }
    }
  }

  // Update header input placeholder with appropriate keyboard shortcut
  updatePlaceholderWithShortcut() {
    if (this.hasHeaderInputTarget) {
      // Detect if user is on Mac
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform)
      const shortcut = isMac ? '⌘K' : 'Ctrl+K'
      
      // Update placeholder to include shortcut hint
      this.headerInputTarget.placeholder = `Search (${shortcut})`
    }
  }
  
  // Pre-populate search inputs with current search query from URL
  prePopulateSearchQuery() {
    const urlParams = new URLSearchParams(window.location.search)
    const searchQuery = urlParams.get('s')
    
    if (searchQuery) {
      // Populate modal input
      this.modalInputTarget.value = searchQuery
      
      // Populate header input if it exists
      if (this.hasHeaderInputTarget) {
        this.headerInputTarget.value = searchQuery
      }
    }
  }
  
  // Fill search input with suggested topic and trigger search
  fillAndSearchTopic(event) {
    const topic = event.currentTarget.dataset.topic
    
    if (topic) {
      // Fill the modal input
      this.modalInputTarget.value = topic
      
      // Sync to header input if it exists
      if (this.hasHeaderInputTarget) {
        this.headerInputTarget.value = topic
      }
      
      // Hide autosuggest
      this.hideAutosuggest()
      
      // Show loading state
      this.showSearchLoading()
      
      // Redirect to WordPress search results
      const searchUrl = new URL(window.location.origin)
      searchUrl.searchParams.set('s', topic)
      
      window.location.href = searchUrl.toString()
    }
  }
  
  // Handle input for autosuggest
  handleAutosuggestInput(event) {
    const query = event.target.value.trim()
    
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    
    // Reset selection
    this.selectedSuggestionIndex = -1
    
    if (query.length < 2) {
      this.hideAutosuggest()
      return
    }
    
    // Show loading state immediately
    this.showLoadingState()
    
    // Debounce the API call
    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(query)
    }, 300)
  }
  
  // Handle keyboard navigation in autosuggest
  handleAutosuggestKeydown(event) {
    if (!this.hasAutosuggestTarget || this.autosuggestTarget.classList.contains(this.hiddenClass)) {
      // If autosuggest is not visible, let normal form submission happen
      return
    }
    
    const suggestions = this.autosuggestTarget.querySelectorAll('[data-suggestion]')
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedSuggestionIndex = Math.min(this.selectedSuggestionIndex + 1, suggestions.length - 1)
        this.updateSuggestionSelection(suggestions)
        break
        
      case 'ArrowUp':
        event.preventDefault()
        this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1)
        this.updateSuggestionSelection(suggestions)
        break
        
      case 'Enter':
        // Always prevent default when suggestions are visible
        event.preventDefault()
        if (this.selectedSuggestionIndex >= 0 && suggestions[this.selectedSuggestionIndex]) {
          // Navigate to selected post
          this.selectPost(suggestions[this.selectedSuggestionIndex])
        } else {
          // No suggestion selected, hide autosuggest and submit the form
          this.hideAutosuggest()
          this.submitSearch(event)
        }
        break
        
      case 'Escape':
        this.hideAutosuggest()
        break
    }
  }
  
  // Fetch suggestions from API
  async fetchSuggestions(query) {
    try {
      const response = await fetch(`/wp-json/whichcar/v1/autosuggest?q=${encodeURIComponent(query)}&limit=8`)
      const data = await response.json()
      if (data.success && data.data.length > 0) {
          this.showSuggestions(data.data)
        } else if (data.success && data.data.length === 0) {
        this.showNoResults()
      } else {
        this.hideAutosuggest()
      }
    } catch (error) {
      console.warn('Autosuggest failed:', error)
      this.hideAutosuggest()
    }
  }
  
  // Show loading state
  showLoadingState() {
    if (!this.hasAutosuggestTarget) return
    
    this.selectedSuggestionIndex = -1
    
    // Show loading spinner
    const loadingHtml = `
      <div class="px-4 py-4 text-center">
        <div class="flex items-center justify-center space-x-2">
          <svg class="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-sm text-gray-500">Searching...</span>
        </div>
      </div>
    `
    
    this.autosuggestTarget.innerHTML = loadingHtml
    this.autosuggestTarget.classList.remove(this.hiddenClass)
  }
  
  // Show suggestions dropdown
  showSuggestions(suggestions) {
    if (!this.hasAutosuggestTarget) return
    
    this.suggestions = suggestions
    this.selectedSuggestionIndex = -1
    
    // Check if we have a "no-results" post type
    if (suggestions.length === 1 && suggestions[0].post_type === 'no-results') {
      // Show "no results found" message
      const noResultsHtml = `
        <div class="px-4 py-4 text-center">
          <div class="text-sm text-gray-500">${this.escapeHtml(suggestions[0].title)}</div>
          <div class="text-xs text-gray-400 mt-1">${this.escapeHtml(suggestions[0].excerpt)}</div>
        </div>
      `
      
      this.autosuggestTarget.innerHTML = noResultsHtml
      this.autosuggestTarget.classList.remove(this.hiddenClass)
      return
    }
    
    // Build suggestions HTML for post results
    const html = suggestions.map((post, index) => `
      <button
        type="button"
        class="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
        data-suggestion="${index}"
        data-url="${this.escapeHtml(post.url)}"
        data-action="click->site-search#selectPostFromClick"
      >
        <div class="font-medium text-gray-900 leading-tight">${this.escapeHtml(post.title)}</div>
        ${post.excerpt ? `<div class="text-xs text-gray-500 mt-1 leading-relaxed">${this.escapeHtml(post.excerpt)}</div>` : ''}
      </button>
    `).join('')
    
    this.autosuggestTarget.innerHTML = html
    this.autosuggestTarget.classList.remove(this.hiddenClass)
  }
  
  // Show no results message
  showNoResults() {
    if (!this.hasAutosuggestTarget) return
    
    this.selectedSuggestionIndex = -1
    
    // Show "no results found" message
    const noResultsHtml = `
      <div class="px-4 py-4 text-center">
        <div class="text-sm text-gray-500">No posts found</div>
      </div>
    `
    
    this.autosuggestTarget.innerHTML = noResultsHtml
    this.autosuggestTarget.classList.remove(this.hiddenClass)
  }
  
  // Hide suggestions dropdown
  hideAutosuggest() {
    if (this.hasAutosuggestTarget) {
      this.autosuggestTarget.classList.add(this.hiddenClass)
      this.selectedSuggestionIndex = -1
    }
  }
  
  // Update visual selection of suggestions
  updateSuggestionSelection(suggestions) {
    suggestions.forEach((suggestion, index) => {
      suggestion.classList.toggle('bg-gray-50', index === this.selectedSuggestionIndex)
    })
  }
  
  // Select post from click
  selectPostFromClick(event) {
    event.preventDefault()
    this.selectPost(event.currentTarget)
  }
  
  // Select a post result
  selectPost(postElement) {
    const url = postElement.dataset.url
    
    if (url) {
      // Hide suggestions
      this.hideAutosuggest()
      
      // Navigate to the post
      window.location.href = url
    }
  }
  
  // Escape HTML
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  // Handle pageshow event (fired when navigating back)
  handlePageShow(event) {
    // Reset search button loading state if needed
    this.resetSearchLoading()
    
    // If modal is open and we're coming from cache (back navigation)
    if (!this.overlayTarget.classList.contains(this.hiddenClass) && event.persisted) {
      // Re-focus the search input
      requestAnimationFrame(() => {
        this.modalInputTarget.focus()
      })
    }
  }
  
  // Check if modal should be open on page load
  checkModalStateOnLoad() {
    // If the modal is visible (not hidden), update our state
    if (!this.overlayTarget.classList.contains(this.hiddenClass)) {
      this.isOpen = true
      
      // Ensure body scroll is prevented
      this.originalBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      
      // Add escape key listener
      document.addEventListener('keydown', this.escapeHandler)
      
      // Reset search button state
      this.resetSearchLoading()
      
      // Focus the input
      requestAnimationFrame(() => {
        this.modalInputTarget.focus()
      })
    }
  }
}
